import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ViewHeader } from '../shell/ViewHeader'
import { type Kpi, KpiRow, spanDays } from '../components/AssetBits'
import { Hint } from '../components/Hint'
import { Card, Note, Skeleton } from '../components/ui'
import { Segmented } from '../components/Tabs'
import { type ChannelKpi, type ChartSeries, api } from '../lib/api'
import { formatAxisDate, formatRange, formatReadoutDate } from '../lib/dates'
import { type LanguageGroup, type Scope, precedingRange, useFilters } from '../lib/filters'
import { abbreviate, formatNumber } from '../lib/numbers'

/**
 * **Channel Overview + Channel Daily** — `APPLICATION_SPEC.md` §4.4 and §4.1.
 *
 * Built before Stories deliberately: it reads only `profile_stats_daily`, needs
 * no ClickUp, and has **zero blocked columns** — the shortest path from data to
 * something Jesse recognises. And Channel Daily is the screen he pointed at.
 *
 * The sidebar owns the seven sections; SS1's tab row lives *inside* one, which
 * is why the three views here are tabs rather than routes.
 */

/**
 * 🔴 **There is no tab row here any more — CO-1 and CO-3, 10 Sep.**
 *
 * This page had three tabs. *Channel daily* was a second mount of the same
 * component `/stories` already renders, and the client's own doc files those
 * tables under Stories, so it stays there and is gone from here. *Coverage*
 * moved into the Snap card on `/connections`, where a data-completeness
 * question belongs. What is left is one view, so a tab row of one is no row.
 */
/** Tabular figures, per the design system — numbers are Plus Jakarta Sans, not mono. */
const numberStyle = { fontVariantNumeric: 'tabular-nums' } as const

/** `all` is not a value the API takes: it means "do not filter by group". */
function groupParam(scope: Scope): LanguageGroup | undefined {
  return scope === 'all' ? undefined : scope
}

/**
 * **Stacked bars on one y axis — CO-10, 24 Sep client meeting.**
 *
 * Public Story Views and Saved Story Views, stacked per day, **one** axis, with
 * counts and % share in the tooltip. This reverses the 18 Sep two-axis
 * decision, which Abi asked for because saved runs ~6% of the estate and ~1% per
 * channel per day (Phony Texts, 10 Aug: 5,113,484 vs 55,835) — so on one scale
 * the saved segment is a sliver whose *shape* cannot be read.
 *
 * **That cost is real and CO-11 is the answer to it, not a comment.** The page's
 * *% share* toggle stacks every bar to 100%, which makes the saved trend legible
 * without reintroducing a second scale; the tooltip shows both figures in
 * either mode.
 *
 * Identity is never colour alone (tritan separation on blue↔mint is 5.9): the
 * legend names both series, and a 1px surface-coloured gap separates the
 * segments. Solid-vs-dashed has no meaning on a bar.
 */
export function ChannelOverview() {
  const { from, to, channels, group } = useFilters()
  // 🔴 **This page read `channels` and never `group` — X-?, 14 Sep.** Picking
  // *English* or *International* in the one scope control writes `?group=` and
  // **clears `channels`**, so both queries here fell back to the whole estate
  // and nothing on screen moved. The control looked live: its label changed.
  // Absence means this page's default, which is the whole estate — it has an
  // International Combined chart to draw.
  const scope: Scope = group ?? 'all'
  const groupFilter = groupParam(scope)
  // CO-11: one control for the whole page, held in the URL like every other
  // view setting so a shared link reproduces it.
  const [params, setParams] = useSearchParams()
  const visible = visibleSeries(params.get('series'))
  // CO-15: a share of one series is always 100%, so % Share only applies with
  // both on. The URL keeps `share=percent`, so switching the second series back
  // on returns the reader to the view they had.
  const mode: Mode =
    visible.length === 2 && params.get('share') === 'percent' ? 'percent' : 'counts'
  const setVisible = (next: SeriesKey[]) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next.length === 1) updated.set('series', SERIES_PARAM[next[0]!])
        else updated.delete('series')
        return updated
      },
      { replace: true },
    )
  const setMode = (next: Mode) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === 'percent') updated.set('share', 'percent')
        else updated.delete('share')
        return updated
      },
      { replace: true },
    )

  return (
    <div>
      <ViewHeader
        title="Channel Overview"
        // The range is already read out beside the period control, so repeating
        // it here put the same two dates on screen twice, 20px apart.
        status="Channel-Level Daily Figures. Snap's Day Is UTC."
        // Not because a channel lifetime does not exist — it does, and
        // `/stories` → Channel Daily now shows it (CO-8). These five are time
        // series, and a windowless total has no day axis to be drawn on.
        allowLifetime={false}
              // Stories carries a dataset tab row and these two do not, so their
        // KPI rows would sit 50px higher without its height reserved (X-12).
        reserveTabRow
      />

      <EstateTotals from={from} to={to} channels={channels} group={groupFilter} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <SeriesToggle visible={visible} onChange={setVisible} />
        <Segmented
          label="Show"
          options={
            visible.length === 2
              ? MODES
              : MODES.map((option) =>
                  option.key === 'percent'
                    ? { ...option, disabledReason: 'Turn Both Series On to Compare Their Share.' }
                    : option,
                )
          }
          active={mode}
          onChange={setMode}
        />
      </div>
      <Charts
        from={from}
        to={to}
        channels={channels}
        group={groupFilter}
        mode={mode}
        visible={visible}
      />
    </div>
  )
}

/**
 * **The estate stat row — CO-7.**
 *
 * Dishant, 11 Sep: *"Organize Channel Overview better, just dropping the 5
 * charts in there looks sloppy."* Five identical cards in a wrapping grid is
 * what he was looking at; this gives the page a top.
 *
 * **Four flows, and the choice of four is the interesting part.** The obvious
 * row is Story Views · Saved Story Views · Viewers · Subscribers — and two of
 * those cannot be totalled. `VIEWERS` is deduplicated within a channel and
 * never across them, so summing thirteen counts anyone watching two shows
 * twice; `SUBSCRIBERS` is a stock. Both are real per channel per day, which is
 * why Channel Daily shows them and this row cannot. The server refuses the sum
 * rather than trusting this comment.
 */
function EstateTotals({ from, to, channels, group }: ScopedQuery) {
  const previous = precedingRange(from, to)
  const query = useQuery({
    queryKey: ['channel-kpis', from, to, channels.join(','), group ?? 'all'],
    queryFn: () => api.channelKpis({ from, to, channels, group }),
    // X-16: keep the panel on screen while a range change refetches. The header
    // survives here (it is rendered by the page, not this component), so the
    // symptom is a flash rather than the whole page going — but it is the same
    // cause and the same one-line remedy.
    placeholderData: (previous) => previous,
  })
  // CO-14: the same tiles over the preceding window of equal length.
  const before = useQuery({
    queryKey: ['channel-kpis', previous.from, previous.to, channels.join(','), group ?? 'all'],
    queryFn: () => api.channelKpis({ from: previous.from, to: previous.to, channels, group }),
  })

  if (query.isLoading) return <Skeleton rows={1} />
  if (query.error || !query.data) return null

  const prior = new Map((before.data ?? []).map((kpi) => [kpi.metric, kpi]))
  return (
    <KpiRow
      kpis={query.data.map((kpi) => ({
        metric: kpi.metric,
        label: KPI_LABELS[kpi.metric],
        value: kpi.value,
        withheld: kpi.value === null,
        ...comparison(kpi, prior.get(kpi.metric), previous),
      }))}
      notes={KPI_NOTES}
      span={{ days: spanDays(from, to) }}
    />
  )
}

/**
 * **CO-14 — the tile's change against the preceding window, or none.**
 *
 * The Stories rule, for the same reason: both windows must hold the **same
 * number of days**, because history starts 20 Jul and a prior window can be
 * the right length and still short — a 23-day total against a 30-day one reads
 * as a fall that never happened.
 *
 * **Subscribers Gained changes by a count, never a percentage** (Nilay, 7 Oct).
 * It is a net figure, negative on 860 rows, so a percentage of a small or
 * negative base is arithmetic without meaning: −200 → +50 is "−125%".
 */
function comparison(
  now: ChannelKpi,
  then: ChannelKpi | undefined,
  previous: { from: string; to: string },
): Pick<Kpi, 'delta' | 'deltaLabel' | 'deltaFormat' | 'previous'> {
  const deltaLabel = `Against ${formatRange(previous.from, previous.to)}, the Preceding Window of Equal Length`
  if (now.value === null || !then || then.value === null || then.days === 0 || then.days !== now.days) {
    return { delta: null }
  }
  if (now.metric === 'SUBSCRIBERS_GAINED') {
    return { delta: now.value - then.value, deltaFormat: 'absolute', deltaLabel, previous: then.value }
  }
  if (then.value === 0) return { delta: null }
  return { delta: (now.value - then.value) / then.value, deltaLabel, previous: then.value }
}

/** Their own names, not Snap's. `SAVED_STORY_VIEWS` shares a label with
 *  `STORY_VIEWS` in the shared map, which is fine in a table column and
 *  ambiguous in a row of four tiles. */
const KPI_LABELS: Record<string, string> = {
  STORY_VIEWS: 'Story Views',
  SAVED_STORY_VIEWS: 'Saved Story Views',
  SPOTLIGHT_VIEWS: 'Spotlight Views',
  SUBSCRIBERS_GAINED: 'Subscribers Gained',
}

const KPI_NOTES: Record<string, string> = {
  STORY_VIEWS: 'Times a Story Was Opened, Across Every Channel in This Selection.',
  SAVED_STORY_VIEWS: 'Times a Saved Story Was Opened.',
  SPOTLIGHT_VIEWS: 'Times a Spotlight Was Opened.',
  SUBSCRIBERS_GAINED: 'Net Change over This Range, so It Can Be Negative.',
}

interface ScopedQuery {
  from: string
  to: string
  channels: string[]
  group?: LanguageGroup
}

function Charts({
  from,
  to,
  channels,
  group,
  mode,
  visible,
}: ScopedQuery & { mode: Mode; visible: SeriesKey[] }) {
  const query = useQuery({
    // `channels` and `group` are both part of the key, or changing either would
    // leave the cached full-estate series on screen — the bug this whole change
    // fixes, and `group` is the half that was missed the first time.
    queryKey: ['channel-series', from, to, channels.join(','), group ?? 'all'],
    queryFn: () => api.channelSeries({ from, to, channels, group }),
    // X-16: keep the panel on screen while a range change refetches. The header
    // survives here (it is rendered by the page, not this component), so the
    // symptom is a flash rather than the whole page going — but it is the same
    // cause and the same one-line remedy.
    placeholderData: (previous) => previous,
  })

  if (query.isLoading) return <Skeleton rows={6} />
  if (query.error) return <Note tone="error">{(query.error as Error).message}</Note>

  const series = query.data ?? []
  if (series.length === 0) {
    return (
      <Note>
        {channels.length || group
          ? 'Nothing in This Selection Has Its Own Chart or Belongs to a Combined One.'
          : 'No Channel Data in This Range.'}
      </Note>
    )
  }

  // CO-7's hierarchy. The three English channels are **small multiples** — the
  // design system's own answer to a fourth series — so they belong in one row at
  // equal width, read against each other. The two combined charts are the
  // estate-level answer and get full width beneath them.
  // The English spotlight chart (CO-12) sits directly under English Combined,
  // which the API already orders; it is neither a per-channel chart nor a
  // stacked one.
  const isCombined = (chart: ChartSeries) =>
    chart.key.endsWith('_combined') || chart.key === SPOTLIGHT_CHART
  const combined = series.filter(isCombined)
  const perChannel = series.filter((chart) => !isCombined(chart))

  return (
    <>
      {perChannel.length > 0 && (
        // `min()` in the track, not a bare figure: a fixed minimum wider than a
        // phone forces the grid past the viewport and scrolls the body. At three
        // across each chart is a third of the width, which is what makes them
        // comparable rather than three separate cards.
        <div
          className="mb-4 grid items-stretch gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(20rem, 100%), 1fr))' }}
        >
          {perChannel.map((chart) => (
            <ViewsChart key={chart.key} chart={chart} mode={mode} visible={visible} compact />
          ))}
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        {combined.map((chart) =>
          chart.key === SPOTLIGHT_CHART ? (
            <SpotlightChart key={chart.key} chart={chart} />
          ) : (
            <ViewsChart key={chart.key} chart={chart} mode={mode} visible={visible} />
          ),
        )}
      </div>
    </>
  )
}

type Mode = 'counts' | 'percent'

const MODES: { key: Mode; label: string; disabledReason?: string }[] = [
  { key: 'counts', label: 'Counts' },
  { key: 'percent', label: '% Share' },
]

/** CO-12's chart key, as the API sends it. */
const SPOTLIGHT_CHART = 'english_spotlights'

/**
 * The two stacked series, public stories at the base — they are the larger
 * part on every channel, so the saved segment reads as what sits on top.
 *
 * One definition feeds the legend, the bars and the tooltip, so none of them
 * can disagree about which colour belongs to which metric.
 */
const SERIES = [
  { name: 'Public Story Views', key: 'story_views', colour: 'var(--data-1)' },
  { name: 'Saved Story Views', key: 'saved_story_views', colour: 'var(--data-2)' },
] as const

type SeriesKey = (typeof SERIES)[number]['key']

/** CO-15's URL values — words a shared link reads plainly, not the API's keys. */
const SERIES_PARAM: Record<SeriesKey, string> = {
  story_views: 'public',
  saved_story_views: 'saved',
}

/** Absent, or anything unrecognised, means both: a stale link never blanks a chart. */
function visibleSeries(param: string | null): SeriesKey[] {
  const only = SERIES.find((series) => SERIES_PARAM[series.key] === param)
  return only ? [only.key] : SERIES.map((series) => series.key)
}

const CHART_MARGIN = { top: 4, right: 8, bottom: 0, left: 8 } as const
const Y_AXIS_WIDTH = 56

/** English Combined and its spotlight chart share a hover (CO-12), by date. */
const ENGLISH_SYNC = 'english-combined'

function yTickFormatter(value: number) {
  return value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000
      ? `${Math.round(value / 1_000)}k`
      : String(value)
}

function percentTick(value: number) {
  return `${Math.round(value * 100)}%`
}

function share(part: number, total: number): string {
  if (!total) return '—'
  const pct = (part / total) * 100
  // One decimal below 10%: the saved share lives at ~1%, where a whole number
  // would print 1% for anything from 0.5 to 1.5.
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`
}

/**
 * **One key for the whole page.** Every stacked chart here draws the same two
 * series in the same two colours, so they are named once, beside the Counts /
 * % Share toggle, rather than in every card — which is what lets a small
 * multiple's readout be colour squares and numbers on one line. The design
 * system's two-series legend rule is met by this, not by repeating it per card.
 *
 * **CO-15 — the key is also the switch** (Jesse, 13 Oct: *"toggle Saved
 * Stories and Public Stories on and off, so I can see if Saved Stories are
 * increasing"*). Saved runs ~1% of a stacked bar, so on its own, with the axis
 * rescaled to it, is the only view where its trend can be read in counts. The
 * last series on cannot be switched off — an empty chart answers nothing.
 */
function SeriesToggle({
  visible,
  onChange,
}: {
  visible: SeriesKey[]
  onChange: (next: SeriesKey[]) => void
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs"
      role="group"
      aria-label="Series"
      data-testid="series-key"
    >
      {SERIES.map((series) => {
        const on = visible.includes(series.key)
        const last = on && visible.length === 1
        const chip = (
          <button
            key={series.key}
            type="button"
            aria-pressed={on}
            aria-disabled={last || undefined}
            onClick={
              last
                ? undefined
                : () =>
                    onChange(
                      on
                        ? visible.filter((key) => key !== series.key)
                        : SERIES.map((s) => s.key).filter(
                            (key) => key === series.key || visible.includes(key),
                          ),
                    )
            }
            className="flex items-center gap-1.5 px-2 py-1"
            style={{
              background: on ? 'var(--bg-surface)' : 'transparent',
              border: `1px solid ${on ? 'var(--border-strong)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)',
              color: on ? 'var(--text-secondary)' : 'var(--text-label)',
              textDecoration: on ? 'none' : 'line-through',
              cursor: last ? 'not-allowed' : 'pointer',
            }}
          >
            <svg width="10" height="10" aria-hidden="true">
              {/* Off is an outline, so the state never rests on the text alone. */}
              <rect
                x="0.5"
                y="0.5"
                width="9"
                height="9"
                rx="2"
                fill={on ? series.colour : 'none'}
                stroke={series.colour}
              />
            </svg>
            {series.name}
          </button>
        )
        return last ? (
          <Hint key={series.key} label="At Least One Series Stays On." tabIndex={-1} className="flex">
            {chip}
          </Hint>
        ) : (
          chip
        )
      })}
    </div>
  )
}

/**
 * **The legend is the readout — CO-13, Abi's 28 Sep review.**
 *
 * The tooltip carried everything needed, and on a 9rem small multiple it covered
 * the whole plot, including the bars a reader wanted to compare against — and on
 * the synced English charts it covered several panels at once. So nothing is
 * drawn over the chart any more: this row shows the **hovered** day, and the
 * latest day when nothing is hovered, and the hovered bar keeps its highlight.
 *
 * Counts **and** share on every stacked chart, in either mode, as the tooltip did.
 * The date is always shown, so a reader knows which day the numbers are — "latest"
 * and "hovered" are the same row and must never be mistaken for each other.
 *
 * One line at every width, never wrapping, so hovering can never make one of three
 * side-by-side cards grow taller than its neighbours, move the total to another
 * line, or shift the chart under the pointer. On a small multiple the series are
 * colour squares — named by `SeriesKey` above the charts and on hover. The date
 * is `Sep 09, 2026` everywhere on this surface (`formatReadoutDate`).
 */
function Readout({
  day,
  hovered,
  items,
  total,
  compact,
}: {
  day: string | undefined
  hovered: boolean
  items: { name: string; colour: string; value: number | undefined }[]
  total?: number
  compact: boolean
}) {
  const count = (value: number | undefined) =>
    value === undefined ? '—' : compact ? abbreviate(value) : formatNumber(value)

  // 🔴 **A fixed layout, never flex-wrap.** Wrapping let the *date's* length
  // decide the layout: "Latest 09/09/26" is longer than a hovered "08/28/26", so
  // on a small multiple Total fell to a second line at rest and not on hover, and
  // the series slid sideways as the pointer came and went (Nilay, 28 Sep). Now
  // the date has a fixed-width slot, the total its own place, and a small
  // multiple is always two lines — date and total, then the series.
  // The date is always the same length ("09/09/26"), so nothing after it moves
  // between hovered and not. Which state it is — the bold hovered day or the
  // latest day at rest — is said by weight, and on a wide chart by a trailing
  // "Latest day" note, where its length cannot shift anything.
  const date = (
    <span
      className="whitespace-nowrap"
      style={{
        ...numberStyle,
        color: hovered ? 'var(--text-primary)' : 'var(--text-label)',
        fontWeight: hovered ? 600 : 400,
      }}
    >
      {day ?? '—'}
    </span>
  )
  const totalCell =
    total !== undefined ? (
      <span className="whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
        Total <strong style={numberStyle}>{count(total)}</strong>
      </span>
    ) : null
  const swatch = (colour: string) => (
    <svg width="10" height="10" aria-hidden="true">
      <rect width="10" height="10" rx="2" fill={colour} />
    </svg>
  )
  const series = items.map((item) => (
    <span
      key={item.name}
      className="flex items-center gap-1.5 whitespace-nowrap"
      style={{ color: 'var(--text-secondary)' }}
    >
      {compact ? (
        // The colour stands in for the name here — the page key above the charts
        // says which is which, and hovering the square names it.
        <Hint label={item.name} className="flex items-center">
          {swatch(item.colour)}
        </Hint>
      ) : (
        <>
          {swatch(item.colour)}
          {item.name}
        </>
      )}
      <strong style={numberStyle}>{count(item.value)}</strong>
      {total !== undefined && item.value !== undefined && (
        <span style={{ ...numberStyle, color: 'var(--text-label)' }}>
          {share(item.value, total)}
        </span>
      )}
    </span>
  ))

  return (
    <div className="mb-2 text-xs" aria-live="polite" data-testid="chart-readout">
      {compact ? (
        // One line on a small multiple too (Nilay, 28 Sep: two lines wasted the
        // card's height). It fits because the names are the page key's job.
        // 8px gaps, not 12: at 1360px a third-width card is 306px, and the long
        // date form left the row 5–9px too wide at the wider spacing.
        <div className="flex items-center gap-2">
          {date}
          {series}
          {totalCell}
        </div>
      ) : (
        // One group, read left to right: the day, what made it up, the total.
        <div className="flex items-center gap-4">
          {date}
          {series}
          {totalCell}
          {!hovered && (
            <span className="whitespace-nowrap" style={{ color: 'var(--text-label)' }}>
              Latest day
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Recharts renders a `<Tooltip>`'s content on every render, with `active: false`
 * when nothing is hovered — and on a synced chart when its partner is hovered.
 * So an empty content component is how the readout learns the day without a box
 * being drawn. State is set from an effect, never during render.
 */
function HoverReporter({
  active,
  label,
  onChange,
}: {
  active?: boolean
  label?: unknown
  onChange: (day: string | null) => void
}) {
  const day = active && label !== undefined ? String(label) : null
  useEffect(() => onChange(day), [day, onChange])
  return null
}

function coverage(chart: ChartSeries) {
  const partial = chart.channels.length < chart.channels_available
  return (
    <span
      className="text-xs"
      style={{
        // A partial selection is called out rather than left to be inferred: a
        // chart titled "International Combined" that quietly summed 3 of 9
        // channels would still look entirely plausible.
        color: partial ? 'var(--text-primary)' : 'var(--text-label)',
        fontWeight: partial ? 600 : 400,
      }}
    >
      {chart.channels_available === 1
        ? '1 channel'
        : partial
          ? `${chart.channels.length} of ${chart.channels_available} channels, filtered`
          : `${chart.channels.length} channels combined`}
    </span>
  )
}

function ViewsChart({
  chart,
  mode,
  visible,
  compact = false,
}: {
  chart: ChartSeries
  mode: Mode
  visible: SeriesKey[]
  compact?: boolean
}) {
  const data = chart.points.map((point) => ({
    day: formatAxisDate(point.stat_date),
    iso: point.stat_date,
    story_views: point.story_views,
    saved_story_views: point.saved_story_views,
  }))
  const [hoveredDay, setHoveredDay] = useState<string | null>(null)
  const shown = (hoveredDay && data.find((point) => point.day === hoveredDay)) || data.at(-1)
  const percent = mode === 'percent'
  const drawn = SERIES.filter((series) => visible.includes(series.key))
  const both = drawn.length === SERIES.length

  // Shorter as a small multiple — three side by side at full height would push
  // the combined charts below the fold.
  const height = compact ? '9rem' : '13rem'

  return (
    <Card
      title={chart.title}
      info={
        !both
          ? `Each Bar Is One Day of ${drawn[0]!.name}, on Its Own Scale. Turn the Other Series Back On Above to Stack Them.`
          : percent
            ? 'Each Bar Is One Day, Stacked to 100%, So the Saved Share Is Readable. Hover a Bar for Its Counts, Shown Above the Chart.'
            : 'Each Bar Is One Day: Public Story Views Plus Saved Story Views, on One Scale. Saved Runs About 1–12% of the Total, So Turn Public Off or Switch to % Share to Read Its Trend.'
      }
      action={coverage(chart)}
    >
      <Readout
        compact={compact}
        day={shown ? formatReadoutDate(shown.iso) : undefined}
        hovered={hoveredDay !== null}
        items={drawn.map((series) => ({
          name: series.name,
          colour: series.colour,
          value: shown?.[series.key],
        }))}
        // From the raw values in either mode — never from the 0–1 fraction
        // `expand` stacks on. One series has no total and no share to show.
        total={both && shown ? shown.story_views + shown.saved_story_views : undefined}
      />

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={CHART_MARGIN}
            // `expand` normalises every stack to 0–1: the % share mode.
            stackOffset={percent ? 'expand' : 'none'}
            barCategoryGap="12%"
            syncId={chart.key === 'english_combined' ? ENGLISH_SYNC : undefined}
            syncMethod="value"
          >
            <CartesianGrid stroke="var(--grid-line)" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
              stroke="var(--axis-line)"
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
              stroke="var(--axis-line)"
              width={Y_AXIS_WIDTH}
              tickFormatter={percent ? percentTick : yTickFormatter}
            />
            <Tooltip
              cursor={{ fill: 'var(--data-1-fill)' }}
              isAnimationActive={false}
              content={(props) => (
                <HoverReporter active={props.active} label={props.label} onChange={setHoveredDay} />
              )}
            />
            {drawn.map((series) => (
              <Bar
                key={series.key}
                dataKey={series.key}
                name={series.name}
                stackId="views"
                fill={series.colour}
                // A surface-coloured edge separates the two segments, so the
                // boundary does not rest on the blue↔mint contrast alone.
                stroke="var(--bg-surface)"
                strokeWidth={1}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

/**
 * **CO-12 — English Combined Spotlight Views**, directly under English Combined.
 *
 * Jesse, 24 Sep: does spotlight viewership trend with stories? Same members,
 * same dates, and the **same hover**: `syncId` with `syncMethod="value"` lines
 * the two charts up by date rather than by array index, so a day missing from
 * one series cannot shift the other's highlight.
 *
 * `--data-3` is the design system's reserved Spotlight Views colour. Bars, to
 * read against the stacked bars above (assumed default — EDITS doc §9).
 */
function SpotlightChart({ chart }: { chart: ChartSeries }) {
  const data = chart.points.map((point) => ({
    day: formatAxisDate(point.stat_date),
    iso: point.stat_date,
    spotlight_views: point.spotlight_views ?? 0,
  }))
  const [hoveredDay, setHoveredDay] = useState<string | null>(null)
  const shown = (hoveredDay && data.find((point) => point.day === hoveredDay)) || data.at(-1)

  return (
    <Card
      title={chart.title}
      info="Spotlight Views per Day Across the English Channels. Hover Lines Up with English Combined Above, So the Two Can Be Read Day by Day."
      action={coverage(chart)}
    >
      <Readout
        compact={false}
        day={shown ? formatReadoutDate(shown.iso) : undefined}
        hovered={hoveredDay !== null}
        items={[{ name: 'Spotlight Views', colour: 'var(--data-3)', value: shown?.spotlight_views }]}
      />
      <div style={{ height: '10rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={CHART_MARGIN}
            barCategoryGap="12%"
            syncId={ENGLISH_SYNC}
            syncMethod="value"
          >
            <CartesianGrid stroke="var(--grid-line)" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
              stroke="var(--axis-line)"
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
              stroke="var(--axis-line)"
              width={Y_AXIS_WIDTH}
              tickFormatter={yTickFormatter}
            />
            <Tooltip
              cursor={{ fill: 'var(--data-3-fill)' }}
              isAnimationActive={false}
              content={(props) => (
                <HoverReporter active={props.active} label={props.label} onChange={setHoveredDay} />
              )}
            />
            <Bar dataKey="spotlight_views" name="Spotlight Views" fill="var(--data-3)" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Channel Daily lives in components/ChannelDailyTables.tsx — the client's own
// doc files those two tables under its Stories heading, so /stories mounts the
// same component. One nine-column contract, two pages.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Channel × day coverage
// ---------------------------------------------------------------------------

